import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { DailyExportData } from '../../services/technicalApi';

interface GenerationTrendChartProps {
  data: DailyExportData[];
  loading?: boolean;
}

const GenerationTrendChart: React.FC<GenerationTrendChartProps> = ({ 
  data, 
  loading = false 
}) => {
  if (loading) {
    return (
      <div className="h-80 flex items-center justify-center">
        <div className="animate-pulse bg-gray-200 dark:bg-gray-700 w-full h-full rounded-lg"></div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
            {formatDate(label)}
          </p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value.toFixed(1)} kWh
            </p>
          ))}
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Efficiency: {data.export_efficiency.toFixed(1)}%
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Last 7 Days Generation
        </h3>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Daily Energy Trends
        </div>
      </div>

      <div className="h-72 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="2 2" className="opacity-30" stroke="currentColor" />
            <XAxis 
              dataKey="date" 
              tickFormatter={formatDate}
              tick={{ fontSize: 11, fill: 'currentColor' }}
              className="text-gray-600 dark:text-gray-400"
              axisLine={false}
              tickLine={false}
            />
            <YAxis 
              tick={{ fontSize: 11, fill: 'currentColor' }}
              className="text-gray-600 dark:text-gray-400"
              axisLine={false}
              tickLine={false}
              label={{ 
                value: 'Energy (kWh)', 
                angle: -90, 
                position: 'insideLeft',
                style: { textAnchor: 'middle' }
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: '10px' }}
              iconType="rect"
            />
            <Bar
              dataKey="total_export_kwh"
              fill="#10b981"
              name="Total Export"
              radius={[3, 3, 0, 0]}
              animationDuration={600}
            />
            <Bar
              dataKey="total_energy_kwh"
              fill="#3b82f6"
              name="Total Energy"
              radius={[3, 3, 0, 0]}
              animationDuration={600}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Export Efficiency Summary */}
      <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            {data.reduce((sum, item) => sum + item.total_export_kwh, 0).toFixed(0)}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Total Export (kWh)</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            {data.reduce((sum, item) => sum + item.total_energy_kwh, 0).toFixed(0)}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Total Energy (kWh)</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
            {data.length > 0 ? (data.reduce((sum, item) => sum + item.export_efficiency, 0) / data.length).toFixed(1) : 0}%
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Avg Efficiency</div>
        </div>
      </div>
    </div>
  );
};

export default GenerationTrendChart;